/**
 * Quick Add by SKU analytics, the same shape as Recommended Accessories
 * (custom/product/accessory-render.js): an add_to_cart_start when the form is
 * submitted, then the confirmed onlineService and GA4 add_to_cart once the add
 * succeeds. getRecommenderContext tags all three with recommender 'quick_add_sku'
 * and the form's placement.
 *
 * The form adds through cart.php?action=add, which returns no cart-item JSON,
 * so the confirmed events are built from the Storefront cart read after the add.
 */
import { buildOnlineServiceAnalyticsEventFile } from './onlineServiceAnalyticsResourceBuilder';
import { fireAddToCart } from './ga4-events';
import { getMotherShipSku } from './productSkuMap';
import { getRecommenderContext } from './recommender-context';
import { getAddToCartData } from '../../f/atc-analytics';

/**
 * onlineService add_to_cart_start for a submitted quick-add form (SKU and qty filled).
 */
export function trackQuickAddStart(form, sku, quantity) {
    const newEvent = {
        loadNumber: 1,
        scriptLoaded: null,
        interactionsCompleted: null,
        errorMessage: {
            eventName: 'add_to_cart_start',
            eventMessage: '',
            timeStamp: new Date().toISOString(),
        },
    };
    const eventObject = {
        tealium_event: 'add_to_cart_start',
        sku: String(sku).trim(),
        page_type: (typeof window.getPageType === 'function' ? window.getPageType() : null) || 'unknown',
        quantity: Number(quantity),
        ...getRecommenderContext(form),
    };
    const cartId = typeof window.getCartId === 'function' ? window.getCartId() : null;

    buildOnlineServiceAnalyticsEventFile(newEvent, eventObject, cartId);
}

/**
 * Confirmed onlineService + GA4 add_to_cart once a quick add succeeded. Sends
 * nothing when the cart has no line with that SKU.
 */
export function trackQuickAddConfirmed(form, cart, sku, quantity) {
    const item = quickAddLineItem(cart, sku, quantity);
    if (!item) return;
    const context = getRecommenderContext(form);

    getAddToCartData({ data: { line_items: [item] } }, null, null, null, context);
    fireAddToCart(
        { ...item, product_sku: getMotherShipSku(item.product_id) || item.sku },
        item.currency,
        context.recommenderName,
    );
}

/**
 * The cart line a quick add landed on, shaped like an itemAdd line item so both
 * getAddToCartData and fireAddToCart can read it. quantity is the amount just
 * added, not the line's running total. null when no line has that SKU.
 */
export function quickAddLineItem(cart, sku, quantity) {
    const wanted = String(sku).trim().toLowerCase();
    const lines = [
        ...(cart?.lineItems?.physicalItems ?? []),
        ...(cart?.lineItems?.digitalItems ?? []),
    ];
    const line = lines.find(item => (item.sku || '').toLowerCase() === wanted);
    if (!line) return null;

    return {
        product_id: line.productId,
        variant_id: line.variantId,
        sku: line.sku,
        product_name: line.name,
        name: line.name,
        brand_name: line.brand || '',
        purchase_price: line.salePrice,
        sale_price: line.salePrice,
        base_price: line.listPrice ?? null,
        price: line.salePrice,
        quantity: Number(quantity),
        discount: 0,
        category_names: [],
        currency: cart?.currency?.code || 'USD',
    };
}
